/**
 * BrainwaveReferenceCard - Displays brainwave/heart rate state reference
 */
import React, { memo } from 'react';

const HEART_RATE_ZONES = [
  { color: 'bg-blue-500', name: 'Deep Rest', range: '10-40 BPM' },
  { color: 'bg-green-500', name: 'Resting', range: '40-60 BPM' },
  { color: 'bg-yellow-500', name: 'Normal', range: '60-80 BPM' },
  { color: 'bg-orange-500', name: 'Elevated', range: '80-90 BPM' },
  { color: 'bg-red-500', name: 'High', range: '90-100 BPM' },
];

const BrainwaveReferenceCard = memo(({
  beatType,
  brainwaveRanges,
  currentBrainwave,
  currentFreq,
  getBrainwaveDescription,
}) => {
  const isHeartbeat = beatType === 'heartbeat';

  return (
    <>
      {isHeartbeat ? (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {HEART_RATE_ZONES.map((zone) => (
            <div key={zone.name} className="text-center">
              <div className={`w-full h-8 rounded-md mb-2 ${zone.color}`} />
              <div className="text-white font-medium">{zone.name}</div>
              <div className="text-white/70 text-sm">{zone.range}</div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Horizontal scrubber line — a single coloured marker that physically
              slides across the band grid as the playhead crosses band
              boundaries. Pure derived position from `currentFreq` against the
              full range. Only meaningful on the md+ horizontal grid layout
              (mobile stacks vertically, so we hide it there). */}
          {(() => {
            const entries = Object.entries(brainwaveRanges);
            if (entries.length === 0 || typeof currentFreq !== 'number') return null;
            // Compute scrubber X position (0–100%) by finding which band the
            // current frequency belongs to and how far into it we are. Cards
            // are equal-width columns, so the fraction maps directly to %.
            const colFrac = 1 / entries.length;
            const overallMin = entries[0][1].min;
            const overallMax = entries[entries.length - 1][1].max;
            let scrubberX;
            if (currentFreq <= overallMin) {
              scrubberX = 0;
            } else if (currentFreq >= overallMax) {
              scrubberX = 100;
            } else {
              let bandIndex = entries.findIndex(
                ([, r]) => currentFreq >= r.min && currentFreq <= r.max
              );
              if (bandIndex < 0) bandIndex = 0;
              const [, range] = entries[bandIndex];
              const span = range.max - range.min;
              const local = span > 0 ? (currentFreq - range.min) / span : 0;
              scrubberX = (bandIndex * colFrac + local * colFrac) * 100;
            }
            const accent = currentBrainwave?.color || '#9CA3AF';
            return (
              <div
                className="relative hidden md:block h-3 mb-2"
                data-testid="brainwave-scrubber"
              >
                {/* Faint full-width rail to imply continuity across the bands. */}
                <div className="absolute inset-x-0 top-1/2 h-px bg-white/10 -translate-y-1/2" />
                {/* Marker — circle with band-coloured glow, slides smoothly. */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-3 w-3 rounded-full"
                  style={{
                    left: `${scrubberX}%`,
                    backgroundColor: accent,
                    boxShadow: `0 0 12px ${accent}, 0 0 4px ${accent}`,
                    transition: 'left 700ms ease-out, background-color 500ms ease-out, box-shadow 500ms ease-out',
                  }}
                  data-testid="brainwave-scrubber-marker"
                />
              </div>
            );
          })()}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {Object.entries(brainwaveRanges).map(([key, range]) => {
            const isActive = currentBrainwave.type === key;
            // Progress through the active band — gives users a visceral sense
            // of "how deep" into Alpha/Theta/etc. the playhead currently is.
            // Clamp to [0,100] since currentFreq can equal min/max exactly.
            const span = range.max - range.min;
            const progressPct = isActive && span > 0 && typeof currentFreq === 'number'
              ? Math.max(0, Math.min(100, ((currentFreq - range.min) / span) * 100))
              : 0;
            return (
              <div
                key={key}
                className={`text-center p-3 rounded-lg transition-all duration-300 ${
                  isActive
                    ? 'border-2 scale-105 shadow-lg'
                    : 'border border-white/20 hover:border-white/40'
                }`}
                style={{
                  borderColor: isActive ? range.color : undefined,
                  backgroundColor: isActive ? `${range.color}20` : 'rgba(255,255,255,0.05)',
                  boxShadow: isActive ? `0 0 20px ${range.color}30` : undefined,
                }}
                data-testid={`brainwave-band-${key}`}
              >
                <div
                  className="w-full h-8 rounded-md mb-2 transition-all duration-300"
                  style={{
                    backgroundColor: range.color,
                    boxShadow: isActive ? `0 0 15px ${range.color}50` : undefined,
                  }}
                />
                <div className="text-white font-medium text-lg">{range.name}</div>
                <div className="text-white/70 text-sm mb-2">
                  {range.min} - {range.max} Hz
                </div>
                <div className="text-white/60 text-xs">
                  {getBrainwaveDescription(key)}
                </div>
                {/* Active-band progress indicator. Empty rail on inactive bands
                    keeps every card the same height (no layout jitter when
                    the active band shifts). */}
                <div
                  className="mt-3 h-1 w-full rounded-full overflow-hidden"
                  style={{
                    backgroundColor: isActive ? `${range.color}25` : 'rgba(255,255,255,0.05)',
                  }}
                  data-testid={`brainwave-band-progress-rail-${key}`}
                >
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: isActive ? `${progressPct}%` : '0%',
                      backgroundColor: range.color,
                      boxShadow: isActive ? `0 0 8px ${range.color}80` : undefined,
                      transition: 'width 600ms ease-out, background-color 400ms ease-out',
                    }}
                    data-testid={`brainwave-band-progress-fill-${key}`}
                  />
                </div>
                {isActive && (
                  <div
                    className="mt-1 text-[10px] uppercase tracking-wide font-medium"
                    style={{ color: range.color }}
                    data-testid={`brainwave-band-progress-label-${key}`}
                  >
                    {Math.round(progressPct)}% through
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}
    </>
  );
});

BrainwaveReferenceCard.displayName = 'BrainwaveReferenceCard';

export default BrainwaveReferenceCard;
